import { assertUnreachable } from '@shared/helpers/function.helpers';

export type ReportCoverImageStatus =
  'loading' | 'missing' | 'ready' | 'generating' | 'removing' | 'error';

/**
 * Whether PrintView may treat the cover as settled for PDF capture.
 * `ready` and `missing` are confirmed outcomes. `error` is also settled so
 * browserless does not wait forever after a failed fetch/decode (print then
 * captures the empty cover slot).
 */
export const isCoverSettledStatus = (status: ReportCoverImageStatus): boolean =>
  status === 'ready' || status === 'missing' || status === 'error';

type ShouldShowLiveCoverMapInput = {
  hasCamera: boolean;
  hasStoredImage: boolean;
  status: ReportCoverImageStatus;
  isPrint: boolean;
  /** PrintView has snapshotted or discarded the cover; do not remount Map3d. */
  isCoverFrozen?: boolean;
  /** Client snapshot will be injected; do not mount Map3d in headless Chrome. */
  skipLiveCoverMap?: boolean;
};

/**
 * Whether the cover slot should mount the live 3D map.
 * Print waits for {@link isCoverSettledStatus} so a persisted Gemini image is
 * not replaced by a live map while the fetch is still `loading`.
 */
export const shouldShowLiveCoverMap = (
  input: ShouldShowLiveCoverMapInput,
): boolean => {
  if (input.isCoverFrozen || input.skipLiveCoverMap) return false;
  if (!input.hasCamera || input.hasStoredImage) return false;
  if (input.isPrint) return isCoverSettledStatus(input.status);
  return input.status !== 'loading';
};

export type PrintCoverReadyAction = 'wait' | 'mark-ready' | 'wait-for-map-idle';

type GetPrintCoverReadyActionInput = {
  status: ReportCoverImageStatus;
  hasStoredImage: boolean;
  hasMapboxToken: boolean;
  skipLiveCoverMap?: boolean;
};

/**
 * What print should do before marking the cover map ready.
 * `wait` — stored-cover fetch has not settled.
 * `mark-ready` — skip the live map (stored image, no Mapbox token, or a
 * client snapshot will be injected).
 * `wait-for-map-idle` — mount the live map and wait for a real Mapbox idle.
 */
export const getPrintCoverReadyAction = (
  input: GetPrintCoverReadyActionInput,
): PrintCoverReadyAction => {
  if (input.hasStoredImage) return 'mark-ready';
  if (!isCoverSettledStatus(input.status)) return 'wait';
  if (input.skipLiveCoverMap) return 'mark-ready';
  if (input.hasMapboxToken) return 'wait-for-map-idle';
  return 'mark-ready';
};

/**
 * Whether a Mapbox idle should mark the print cover ready.
 * An empty design's idle is only the transparent style — PrintView must wait
 * for the idle after storeys (the 3D building) have been applied.
 */
export const shouldAcceptPrintCoverMapIdle = (
  isEmptyDesign: boolean,
): boolean => !isEmptyDesign;

/**
 * Map a failed cover-image DELETE to UI status.
 * `404` means the row is already gone; any other failure keeps the local image.
 */
export const getCoverStatusAfterDeleteFailure = (
  httpStatus: number | undefined,
): Extract<ReportCoverImageStatus, 'missing' | 'ready'> =>
  httpStatus === 404 ? 'missing' : 'ready';

/**
 * Whether generate-cover may open.
 * `error` is included so a failed load can be replaced without a delete control.
 */
export const canCreateReportCoverImage = (
  status: ReportCoverImageStatus,
): boolean => status === 'missing' || status === 'error';

/** One-shot Admin Area request consumed by the report cover-image controller. */
export type ReportCoverImageRequest = 'none' | 'create' | 'remove';

export type ReportCoverImageRequestResolution =
  | 'wait'
  | 'open-create'
  | 'confirm-remove'
  | 'reject-has-image'
  | 'reject-no-image';

type ResolveReportCoverImageRequestInput = {
  request: Exclude<ReportCoverImageRequest, 'none'>;
  status: ReportCoverImageStatus;
  isCaptureReady: boolean;
};

/**
 * Decide how the report cover-image controller should honour an Admin Area request.
 * Busy statuses wait so a generate/remove in flight is not interrupted.
 * {@link ReportCoverImageRequest} is set from the profile Admin Area.
 */
export const resolveReportCoverImageRequest = ({
  request,
  status,
  isCaptureReady,
}: ResolveReportCoverImageRequestInput): ReportCoverImageRequestResolution => {
  if (
    status === 'loading' ||
    status === 'generating' ||
    status === 'removing'
  ) {
    return 'wait';
  }

  switch (request) {
    case 'remove':
      return status === 'ready' ? 'confirm-remove' : 'reject-no-image';
    case 'create':
      if (!canCreateReportCoverImage(status)) return 'reject-has-image';
      if (!isCaptureReady) return 'wait';
      return 'open-create';
    default:
      return assertUnreachable(request);
  }
};
